
export interface RazorpayCheckoutOptions {
  keyId: string;
  orderId?: string;
  subscriptionId?: string;
  amount?: number;
  currency?: string;
  name: string;
  description: string;
  userName: string;
  userEmail: string;
  userContact: string;
  notes?: Record<string, string>;
  onSuccess: (response: { 
    razorpay_payment_id?: string;
    razorpay_order_id?: string;
    razorpay_subscription_id?: string;
    razorpay_signature?: string;
  }) => void;
  onDismiss: () => void;
}

export interface RazorpaySubscriptionOptions extends RazorpayCheckoutOptions {}

const loadRazorpaySdk = (): Promise<any> => {
  const existing = (window as any).Razorpay;
  if (existing) return Promise.resolve(existing);

  return new Promise((resolve, reject) => {
    const currentScript = document.querySelector<HTMLScriptElement>('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (currentScript) {
      currentScript.addEventListener('load', () => resolve((window as any).Razorpay), { once: true });
      currentScript.addEventListener('error', () => reject(new Error('Razorpay SDK failed to load')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve((window as any).Razorpay);
    script.onerror = () => reject(new Error('Razorpay SDK failed to load'));
    document.head.appendChild(script);
  });
};

const openRazorpayCheckout = (Razorpay: any, options: RazorpayCheckoutOptions): void => {
  if (!Razorpay) {
    throw new Error('Razorpay SDK loaded without exposing checkout constructor');
  }

  const rzpOptions: any = {
    key: options.keyId,
    name: options.name,
    description: options.description,
    image: "https://picsum.photos/200",
    handler: options.onSuccess,
    prefill: {
      name: options.userName,
      email: options.userEmail,
      contact: options.userContact
    },
    notes: options.notes,
    theme: {
      color: "#4f46e5"
    },
    modal: {
      ondismiss: options.onDismiss
    }
  };

  if (options.subscriptionId) {
    rzpOptions.subscription_id = options.subscriptionId;
  } else if (options.orderId) {
    rzpOptions.order_id = options.orderId;
  }

  if (options.amount) {
    rzpOptions.amount = options.amount;
  }
  if (options.currency) {
    rzpOptions.currency = options.currency;
  }

  const rzp = new Razorpay(rzpOptions);
  rzp.open();
};

/**
 * Service to handle Razorpay Subscriptions and Orders using safe backend-created checkout sessions.
 */
export const processRazorpaySubscription = (options: RazorpayCheckoutOptions): void => {
  loadRazorpaySdk()
    .then((Razorpay) => openRazorpayCheckout(Razorpay, options))
    .catch((error) => {
      console.error('Failed to initialize Razorpay checkout:', error);
      alert('Payment gateway failed to load. Please try again later.');
      options.onDismiss();
    });
};
